import * as hash from 'json-hash';
import * as _ from 'lodash';
import { DateTime } from 'luxon';

import type { DisplaySettings, LinkedAssetInfo } from '@/models/channel';
import { LinkedAssetType } from '@/models/channel';

import { config } from '../config';
import { SizeUnits } from '../enums';
import type { Item, LiveProgram } from '../models';
import type { OU } from '../models/Ou';
import { applyDateRangeTransform, Transform } from '../pages/assets/asset-listing/applyDateRangeTransform';
import { FuelEntityType } from '../services';
import { LocalStorageService } from '../services/local-storage';
import { SchemaType } from '../store/forms/Asset-Editor/SchemaTypeOptions';
import type { DateRangeId } from '../store/slices/asset-listing/DateRangeId';
import { dateUtils } from './dateUtils';

const ALLOWED_IMAGE_TYPES = ['.jpeg', '.jpg', '.png', '.gif', '.svg', '.webp'];
const ALLOWED_VIDEO_TYPES = ['.mov', '.mpeg4', '.mxf', '.wmv', '.mp4', '.mpeg', '.mpg', '.mkv', '.avif'];
const ALLOWED_GRAPHICS_TYPES = ['.mov'];
const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

export type SlugOptions = {
  maxLength?: number; // optional maximum length for the slug
  allowUnicode?: boolean; // if true, keeps unicode letters (e.g. non-latin), otherwise normalizes to ASCII
};

export default {
  parseNumber(value: string | null) {
    if (value == null || value.trim() === '' || Number.isNaN(+value)) {
      return null;
    }
    return +value;
  },
  isGuid(s: string | null) {
    if (this.isNullOrWhitespace(s)) {
      return false;
    }
    return GUID_REGEX.test(s);
  },
  populateDisplaySettings(linkedAssets: LinkedAssetInfo[], displaySettings: DisplaySettings) {
    const newDisplaySettings: DisplaySettings = {
      backgroundColor: '#ffffff',
    };
    if (displaySettings?.backgroundColor != null) {
      newDisplaySettings.backgroundColor = displaySettings.backgroundColor;
    }

    if (linkedAssets?.length > 0) {
      newDisplaySettings.favIcon = linkedAssets?.find((x) => x.type === LinkedAssetType.FavIcon)?.assetId || null;
      newDisplaySettings.thumbnail = linkedAssets?.find((x) => x.type === LinkedAssetType.Thumbnail)?.assetId || null;
      newDisplaySettings.poster = linkedAssets?.find((x) => x.type === LinkedAssetType.Poster)?.assetId || null;
      newDisplaySettings.background = linkedAssets?.find((x) => x.type === LinkedAssetType.Background)?.assetId || null;
      newDisplaySettings.logo = linkedAssets?.find((x) => x.type === LinkedAssetType.Logo)?.assetId || null;
      newDisplaySettings.badge = linkedAssets?.find((x) => x.type === LinkedAssetType.Badge)?.assetId || null;
    }

    return newDisplaySettings;
  },
  populateLinkedAssetsWithDiplaySettings(linkedAssets: LinkedAssetInfo[], displaySettings: DisplaySettings) {
    // console.log('populateLinkedAssetsWithDiplaySettings', { linkedAssets, displaySettings });
    let newLinkedAssets: LinkedAssetInfo[] = [];
    if (linkedAssets != null) {
      newLinkedAssets = _.cloneDeep(linkedAssets);
      newLinkedAssets = newLinkedAssets.filter(
        (x) =>
          x.type !== LinkedAssetType.FavIcon &&
          x.type !== LinkedAssetType.Thumbnail &&
          x.type !== LinkedAssetType.Poster &&
          x.type !== LinkedAssetType.Background &&
          x.type !== LinkedAssetType.Logo &&
          x.type !== LinkedAssetType.Badge
      );
    }
    if (!this.isNullOrWhitespace(displaySettings?.favIcon)) {
      newLinkedAssets.push({ type: LinkedAssetType.FavIcon, assetId: displaySettings?.favIcon });
    }
    if (!this.isNullOrWhitespace(displaySettings?.thumbnail)) {
      newLinkedAssets.push({ type: LinkedAssetType.Thumbnail, assetId: displaySettings?.thumbnail });
    }
    if (!this.isNullOrWhitespace(displaySettings?.poster)) {
      newLinkedAssets.push({ type: LinkedAssetType.Poster, assetId: displaySettings?.poster });
    }
    if (!this.isNullOrWhitespace(displaySettings?.background)) {
      newLinkedAssets.push({ type: LinkedAssetType.Background, assetId: displaySettings?.background });
    }
    if (!this.isNullOrWhitespace(displaySettings?.logo)) {
      newLinkedAssets.push({ type: LinkedAssetType.Logo, assetId: displaySettings?.logo });
    }
    if (!this.isNullOrWhitespace(displaySettings?.badge)) {
      newLinkedAssets.push({ type: LinkedAssetType.Badge, assetId: displaySettings?.badge });
    }
    return newLinkedAssets;
  },
  generateStrongPassword(length = 12) {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=<>?';
    let password = '';
    // Generate initial password with cryptographically strong randomness
    const randomValues = new Uint32Array(length);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < length; i++) {
      password += charset[randomValues[i] % charset.length];
    }
    return password;
  },
  /**
   * Download contents as a file
   * Source: https://stackoverflow.com/questions/14964035/how-to-export-javascript-array-info-to-csv-on-client-side
   */
  exportFile(content: string, filename: string, contentType: string) {
    // Create a blob
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);

    // Create a link to download it
    const pom = document.createElement('a');
    pom.href = url;
    pom.setAttribute('download', filename);
    pom.click();
    setTimeout(() => {
      pom.remove();
    }, 300);
  },

  // returns the file extension to lowercase with the dot eg. getExtension('myfile.mxf') returns '.mxf'
  getExtension(str: string): string {
    if (!str) return null;
    return str.substring(str.lastIndexOf('.')).toLowerCase();
  },

  formatUILogMessage(error) {
    const defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');
    const userInfo = LocalStorageService.getItem('userInfo');
    let stack = null;
    if (error && error.stack) {
      stack = error.stack;
    }
    if (error && error.response && error.response.data) {
      return {
        msg: {
          url:
            error.response.config && error.response.config.url ? error.response.config.baseURL + error.response.config.url : '',
          httpStatusCode: error.response.status,
          ouId: defaultLoginOu,
          user: userInfo,
        },
        serviceName: `Fuel UI v${config.appVersion}`,
        type: 'Error',
        additionalInfo: error.response.data,
      };
    }
    return {
      msg: {
        ouId: defaultLoginOu,
        user: userInfo,
      },
      type: 'Error',
      additionalInfo: _.isString(error) ? error : JSON.stringify(error),
      serviceName: `Fuel UI v${config.appVersion}`,
    };
  },

  checkOUAdTagConfiguration(ouData: OU): boolean {
    // console.log('checkOUAdTagConfiguration', { ouData });
    if (
      !ouData ||
      !ouData.metaData ||
      ouData?.metaData?.adConfiguration == null ||
      this.isNullOrWhitespace(ouData?.metaData?.adConfiguration?.ssaiSupplyTagId) ||
      this.isNullOrWhitespace(ouData?.metaData?.adConfiguration?.csaiSupplyTagId) ||
      this.isNullOrWhitespace(ouData?.metaData?.adConfiguration?.demandPartnerId)
    ) {
      return false;
    }
    return true;
  },

  async sleep(ms: number) {
    // console.log('sleep', { ms });
    return new Promise((resolve) => setTimeout(resolve, ms));
  },

  isNullOrWhitespace(str: string | null | undefined) {
    if (str == null) {
      return true;
    }
    if (typeof str === 'string' && str.trim() === '') {
      return true;
    }
    return false;
  },
  /**
   * Returns a validFation warning message about the acceptable file extensions allowed for that schemaType, else null.
   */
  validateFileExtension(schemaType: SchemaType, fileName: string): string | null {
    // console.log('validateFileExtension', { schemaType, fileName });
    let regex;
    let bodyText;
    let message;

    const allowedFilesForImages = ['.jpeg', '.jpg', '.png', '.gif', '.svg'];
    const allowedFilesForVideo = ['.mov', '.mpeg4', '.mxf', '.wmv', '.mp4', '.mpeg', '.mpg', '.mkv'];
    const allowedFilesForGraphics = ['.mov'];

    if (schemaType === SchemaType.Image) {
      regex = new RegExp(`([\\W\\S])+(${allowedFilesForImages.join('|')})$`, 'i');
      message = allowedFilesForImages.join(', ');
      bodyText = `please upload files having extension ${message} only.`;
    } else if (schemaType === SchemaType.Graphics) {
      regex = new RegExp(`([\\W\\S])+(${allowedFilesForGraphics.join('|')})$`);
      message = allowedFilesForGraphics.join(', ');
      bodyText = `please upload files having extension ${message} only.`;
    } else {
      regex = new RegExp(`([\\W\\S])+(${allowedFilesForVideo.join('|')})$`);
      message = allowedFilesForVideo.join(', ');
      bodyText = `please upload files having extensions: ${message} only.`;
    }

    if (!regex.test(fileName.toLowerCase())) {
      return `Unsupported media content for ${schemaType} - ${bodyText}`;
    }
    return null;
  },

  validateFiles(files: FileList) {
    if (files == null || files.length === 0) {
      return false;
    }
    const fileList = Array.from(files);
    const invalidFiles = fileList.filter((file) => {
      const ext = this.getExtension(file.name);
      return !ALLOWED_IMAGE_TYPES.includes(ext) && !ALLOWED_VIDEO_TYPES.includes(ext) && !ALLOWED_GRAPHICS_TYPES.includes(ext);
    });
    return invalidFiles.length === 0;
  },

  /**
   * Gets a string eg. `type=Clip;id=b31417f3-6e66-4115-9fc9-5dec7d28b8b2`
   */
  getAttachedFuelEntityType(obj: Item | LiveProgram) {
    if (obj == null) {
      return null;
    }
    let sourceType = obj.entityType;
    if (obj.entityType === FuelEntityType.Clip) {
      const clip = obj as Item;
      if (clip && clip.source) {
        if (clip.source === 'ClipGroup') {
          sourceType = 'ClipGroup';
        } else if (clip.source === 'NestedChannel') {
          sourceType = 'NestedChannel';
        }
      }
    }
    return sourceType;
  },

  /**
   * Gets a string eg. `type=Clip;id=b31417f3-6e66-4115-9fc9-5dec7d28b8b2`
   */
  getAttachedFuelEntityId(obj: Item | LiveProgram) {
    if (obj == null) {
      return null;
    }
    const sourceType = this.getAttachedFuelEntityType(obj);
    const attachedFuelEntityId = `type=${sourceType};id=${obj.id}`;
    return attachedFuelEntityId;
  },

  /**
   * Gets a string eg. `type=Clip;id=b31417f3-6e66-4115-9fc9-5dec7d28b8b2`
   */
  parseFuelEntityId(attachedFuelEntityId: string | null) {
    if (attachedFuelEntityId == null) {
      return null;
    }
    const idx = attachedFuelEntityId.lastIndexOf('id=');
    if (idx > -1) {
      return attachedFuelEntityId.substring(idx + 3);
    }
    return null;
  },

  parseFuelEntityType(attachedFuelEntityId: string) {
    if (attachedFuelEntityId == null) {
      return null;
    }
    const endIdx = attachedFuelEntityId.lastIndexOf(';id=');
    const startIdx = attachedFuelEntityId.lastIndexOf('type=');
    if (startIdx > -1 && endIdx > -1) {
      return attachedFuelEntityId.substring(startIdx + 5, endIdx);
    }
    return null;
  },
  parseFuelFlags(str: string) {
    const regex = /([a-z]\w*)=((?:[^"]|"[^"]+")+?)(?=;\s*[a-z]\w*=|$)/g;
    let m;
    const els = [];
    while ((m = regex.exec(str)) !== null) {
      els.push(`"${m[1]}": ${m[2]}`);
    }
    const json = `{${els.join(',')}}`;
    const obj = JSON.parse(json);
    return obj;
  },

  /**
   * Gets a hash to check if a large object has any changes from moment to moment.
   * @param obj Any js object.
   * @returns null if obj is null or undfined, otherwise a hash of the object.
   */
  getChecksum(obj: any) {
    if (obj == null) {
      return null;
    }
    return hash.digest(obj);
  },

  getSchemaTypeByExtension(file: File, includeAsTransition: boolean = false): SchemaType | null {
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const imageExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.svg', '.avif', '.webp'];
    const videoExtensions = ['.mov', '.mpeg4', '.mxf', '.wmv', '.mp4', '.mpeg', '.mpg', '.mkv'];
    const graphicsExtensions = ['.mov'];

    if (imageExtensions.includes(extension)) {
      return SchemaType.Image;
    }
    if (includeAsTransition && graphicsExtensions.includes(extension)) {
      return SchemaType.Graphics;
    }
    if (videoExtensions.includes(extension)) {
      return SchemaType.Video;
    }

    return null;
  },

  /**
   * Returns a value that can be used as a key for a given string
   * by removing all the spaces and casting it to lower case.
   *
   * @param s The string to get a key from
   */
  getKey(s: string): string {
    return s.replace(/ /g, '').toLowerCase().trim();
  },

  swapInArray(arr: any[], from: number, to: number) {
    const newArr = [...arr];
    if (to > newArr.length - 1) {
      newArr.push(newArr.splice(from, 1)[0]);
    } else {
      newArr.splice(to, 0, newArr.splice(from, 1)[0]);
    }
    return newArr;
  },

  durationForDisplay(item: Item): string | null {
    if (item && item.durationString != null) {
      return this.formatDuration(+item.durationString);
    }
    if (item && item.duration != null) {
      return this.formatDuration(item.duration);
    }
    return null;
  },

  /**
   * convertToQueryString
   *
   * @param {*} input
   * @returns {string}
   */
  convertToQueryString(input: any): string {
    // console.log('convertToQueryString', input);
    if (input === null) return null;
    const qs = Object.keys(input)
      .filter((key) => input[key] != null && !this.isNullOrWhitespace(input[key]))
      .map((key) => {
        if (_.isArray(input[key])) {
          return _.map(_.omitBy(input[key], _.isNil), (v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(input[key])}`;
      })
      .join('&');
    // console.log('convertToQueryString', input, qs);
    return qs;
  },

  /**
   * Shorten a filename
   *
   * @param {string} fileName
   * @param {number} maxLength
   * @returns {string}
   */
  shortenFileName(fileName: string, maxLength: number): string {
    let name = fileName.substr(0, fileName.lastIndexOf('.'));
    const ext = fileName.substr(fileName.lastIndexOf('.') + 1, fileName.length);

    if (name.length > maxLength) {
      name = `${name.substr(0, maxLength - 3)}...`;
    }
    return `${name}.${ext}`;
  },

  /**
   *
   * @param {string} utcDateTime
   * @returns {string}
   */
  formatUTCDateTime(dateString: string, format = 'MM-dd-yyyy HH:mm'): string {
    if (dateString == null || dateString == '') {
      return null;
    }
    return DateTime.fromISO(dateString).toFormat(format);
  },

  /**
   *
   * @param {string} utcDateTime
   * @returns {string}
   */
  formatExcelTime(dateString: string): string {
    if (dateString == null || dateString == '') {
      return null;
    }
    return DateTime.fromISO(dateString).toUTC().toFormat('MM-dd-yyyy HH:mm a');
  },

  /**
   *
   * @param {date} dateTime
   * @returns {string}
   */
  formatDateTimeToUTC(dateTime: string, format = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"): string {
    if (dateTime == null || dateTime == '') {
      return null;
    }
    return DateTime.fromISO(dateTime).toUTC().toFormat(format);
  },

  /**
   *
   * @param {string} videoTimeString - video time string
   * @returns {number}
   */
  parseVideoTime(value: string, frameRate: number) {
    let seconds = null;

    // check the string is in the pattern 00:00:00:00
    if (/\d\d:\d\d:\d\d;\d\d/.test(value)) {
      const frameTime = 1 / frameRate;
      const hours = parseInt(value.slice(0, 2), 10) * 3600;
      const minutes = parseInt(value.slice(3, 5), 10) * 60;
      const seconds2 = parseInt(value.slice(6, 8), 10);
      const ms = parseFloat(`${value.slice(9, 11)}`) * frameTime;
      seconds = hours + minutes + seconds2 + ms;
      if (seconds < 0) {
        seconds = 0;
      }
    }
    // console.log('parseVideoTime', value, frameTime, ' > ', seconds);
    return seconds;
  },

  /**
   * Format a for clip tool video format eg '00:00:02;30' with frames on the end
   *
   * @param {number} seconds - time in seconds
   * @param {number} frameRate - frame rate for the video ie. 30 fps
   * @returns {string}
   */
  formatVideoTime(seconds: number, frameRate: number = 30): string {
    if (seconds == null || seconds === 0 || seconds === Infinity || Number.isNaN(seconds)) {
      return '00:00:00;00';
    }
    const frameTime = 1 / frameRate;
    // console.log('formatVideoTime', { seconds, frameRate });
    let frames = Math.round((seconds % 1) / frameTime);
    if (frames === frameRate) {
      frames = 0;
      seconds++;
    }
    const formattedTime = dateUtils.formatDuration(seconds);
    return `${formattedTime};${frames < 10 ? '0' : ''}${frames}`;
  },

  /**
   * Converts timestamp in HH:mm:ss to a number in total seconds.  eg. 00:05:07 -> 307
   */
  convertTimestampToNumber(timestamp: string | null) {
    if (timestamp == null) {
      return null;
    }
    if (typeof timestamp === 'number') {
      return timestamp;
    }
    if (!timestamp.includes(':')) {
      return null;
    }
    const hms = timestamp.split(':');
    const hours = hms[0];
    const minutes = hms[1];
    const seconds = hms[2];
    return +hours * 3600 + +minutes * 60 + +seconds; // the +variable makes it a number
  },
  /**
   * Formats seconds to HH:mm:ss format. eg. 361 > 00:06:01
   */
  formatDuration(seconds: number | null): string {
    if (!seconds) {
      seconds = 0;
    }
    const formattedTime = this.toHHMMSS(seconds);
    return formattedTime;
  },
  toHHMMSS(secs) {
    const sec_num = parseInt(secs, 10);
    const hours = Math.floor(sec_num / 3600);
    const minutes = Math.floor(sec_num / 60) % 60;
    const seconds = sec_num % 60;
    return [hours, minutes, seconds].map((v) => (v < 10 ? `0${v}` : v)).join(':');
  },

  async copyToClipboard(value: string) {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }

    await setTimeout(() => {}, 0);

    // const el = document.createElement('textarea');
    // el.value = value;
    // document.body.appendChild(el);
    // el.select();
    // document.execCommand('copy');
    // document.body.removeChild(el);
  },

  keypress(event) {
    // console.log('event.which', event.which);
    if (event.keyCode == 13 || event.keyCode == 9) {
      // 13 = Enter key, 9 = tab
      const input = this.$el.querySelector('input') as HTMLInputElement;
      input.blur();
    } else if (event.which !== 45 && (event.which < 48 || event.which > 57)) {
      // Reject all non-numeric
      event.preventDefault();
    }
  },
  keypressAndZeroValueWillNotAllow(event) {
    // console.log('event.which', event)
    if (event.keyCode == 13 || event.keyCode == 9) {
      // 13 = Enter key, 9 = tab
      const input = this.$el.querySelector('input') as HTMLInputElement;
      input.blur();
    } else if (event.which < 49 || event.which > 57) {
      // Reject all non-numeric
      event.preventDefault();
    }
  },
  onPaste(evt) {
    evt.preventDefault();
    return false;
  },
  // applying a date range opens up the start->end "window" in both directions, by subtracting from the baseline (created date or expired date) and by
  //   adding to the baseline...so "2020-12-16" with a date range of "3 days" means "2020-12-13" -> "2020-12-19"...
  resolveDateFrom(baseline: string | null, dateRange: { id: DateRangeId; name: string } | string | null) {
    if (baseline === null) return null;

    let dt = DateTime.fromJSDate(baseline).startOf('day').toISO();

    if (dateRange === null || dateRange === '') {
      dateRange = { id: '1-day', name: '+/- 1 Day' };
    }

    dt = applyDateRangeTransform(dt, dateRange.id, Transform.Subtract);
    return dt.toISO();
  },
  resolveDateTo(baseline: string | null, dateRange: { id: DateRangeId; name: string } | string | null) {
    if (baseline === null) return null;
    let dt = DateTime.fromJSDate(baseline).endOf('day').toISO();

    if (dateRange === null || dateRange === '') {
      dateRange = { id: '1-day', name: '+/- 1 Day' };
    }

    dt = applyDateRangeTransform(dt, dateRange.id, Transform.Add);
    return dt.toISO();
  },
  /**
   * Convert filesize to humanly readable format
   *
   * @param {number} size
   * @returns {string}
   */
  calcSize(size: number): string {
    const i: number = Math.floor(Math.log(size) / Math.log(1024));
    const result: string = `${((size / 1024 ** i) * 1).toFixed(2)} ${
      [
        SizeUnits[SizeUnits.Byte],
        SizeUnits[SizeUnits.KB],
        SizeUnits[SizeUnits.MB],
        SizeUnits[SizeUnits.GB],
        SizeUnits[SizeUnits.TB],
        SizeUnits[SizeUnits.PB],
        SizeUnits[SizeUnits.EB],
        SizeUnits[SizeUnits.ZB],
        SizeUnits[SizeUnits.YB],
      ][i]
    }`;
    return result;
  },
  /**
   * Remove file extension from a fileName
   *
   * @param {number} size
   * @returns {string}
   */
  removeExtension(fileName: string): string {
    return fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
  },
  generateRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  },
  generateUniqueRandomColor(): string {
    let color;
    let previousColor = '';
    do {
      // Generate a random dark color
      const r = Math.floor(Math.random() * 150); // limiting to dark range (0-150)
      const g = Math.floor(Math.random() * 150);
      const b = Math.floor(Math.random() * 150);

      color = `rgb(${r}, ${g}, ${b})`;
    } while (color === previousColor); // Ensure the color is not a duplicate

    // Store the new color
    previousColor = color;

    return color;
  },
  secondsToTimeSpanFormat(myVal) {
    let parsedData = { formattedValue: null, seconds: null };
    let hourPart = '00';
    let minutePart = '00';
    let secondPart = '00';
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    let totalSeconds = 0;
    const colonIndex = myVal.indexOf(':');
    if (colonIndex > -1) {
      // find the minutes and seconds from the string
      const hms = myVal.split(':');
      if (hms.length === 3) {
        hourPart = hms[0];
        minutePart = hms[1];
        secondPart = hms[2];
      } else {
        minutePart = hms[0];
        secondPart = hms[1];
      }
    } else if (myVal.length > 2) {
      minutePart = myVal.slice(0, myVal.length - 2);
      secondPart = myVal.slice(myVal.length - 2, myVal.length);
    } else {
      secondPart = myVal;
    }
    hours = Math.floor(parseInt(hourPart || '00'));
    minutes = Math.floor(parseInt(minutePart || '00'));
    seconds = Math.floor(parseInt(secondPart || '00'));
    if (seconds > 59) {
      minutes += Math.floor(seconds / 60);
      seconds %= 60;
    }
    if (minutes > 99) {
      hours += Math.floor(minutes / 60);
      minutes = seconds % 60;
    }
    if (hours > 99) {
      hours = 99;
    }
    totalSeconds = hours * 3600 + minutes * 60 + seconds;
    const formattedValue = dateUtils.formatDuration(totalSeconds);

    parsedData = { formattedValue, seconds: totalSeconds };
    return parsedData;
  },
  isObjectEmpty(objectName) {
    return Object.keys(objectName).length === 0;
  },
  getAssetTitle(filename: string) {
    return filename.replace(/[-_]/g, ' ');
  },
  chunkArray<T>(arr: T[], size: number): T[][] {
    if (!Array.isArray(arr) || size <= 0) {
      throw new Error("Invalid input: 'arr' must be an array and 'size' must be a positive number.");
    }

    const chunked: T[][] = [];

    for (let i = 0; i < arr.length; i += size) {
      chunked.push(arr.slice(i, i + size));
    }

    return chunked;
  },
  slugify(input: string, opts: SlugOptions = {}): string {
    const { maxLength, allowUnicode = false } = opts;
    if (!input) return '';

    let s = input.trim();

    if (!allowUnicode) {
      // Normalize to ASCII by removing diacritics, e.g. "café" -> "cafe"
      s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    } else {
      // still normalize to decompose combined chars but keep letters
      s = s.normalize('NFKD');
    }

    // Lowercase
    s = s.toLowerCase();

    // Replace any sequence of whitespace with a single hyphen
    s = s.replace(/\s+/g, '-');

    // Remove characters that are not letters, numbers, hyphens or underscores.
    // If allowUnicode = true, allow unicode letters (using \p{L}) via Unicode property escapes.
    if (allowUnicode) {
      s = s.replace(/[^\p{L}\p{N}\-_]+/gu, '');
    } else {
      s = s.replace(/[^a-z0-9\-_]+/g, '');
    }

    // Collapse multiple hyphens/underscores to a single hyphen
    s = s.replace(/[-_]{2,}/g, '-');

    // Remove leading/trailing hyphens/underscores
    s = s.replace(/^[-_]+|[-_]+$/g, '');

    // // Enforce max length if provided (don't break words awkwardly: trim trailing hyphen)
    // if (typeof maxLength === 'number' && maxLength > 0) {
    //   s = s.slice(0, maxLength);
    //   s = s.replace(/[-_]+$/g, '');
    // }

    return s;
  },

  isValidM3u8Url(url: string): boolean {
    const regex = /^https?:\/\/[^\s$.?#].[^\s]*\.m3u8([?#].*)?$/i;
    return regex.test(url);
  },

  isValidUrl(url: string): boolean {
    if (url == null) {
      return false;
    }
    if (/\s/.test(url)) {
      return false;
    }
    return URL.canParse(url);
  },
};
